import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  TESSA_ENVIRONMENT_AVAILABILITY_ROUTE_PATH,
  TESSA_ENVIRONMENT_KEY_QUERY_PARAM,
  TESSA_ENVIRONMENT_NAME_QUERY_PARAM,
} from '@ns/plugin-ns-tessa-common';
import { z } from 'zod';

const TESSA_PLUGIN_ID = 'catalog';

export type TessaEnvironmentAvailabilityValue = {
  available: boolean;
  value: string;
};

export type TessaEnvironmentAvailability = {
  key: TessaEnvironmentAvailabilityValue | null;
  name: TessaEnvironmentAvailabilityValue | null;
};

const availabilityValueSchema = z.object({
  available: z.boolean(),
  value: z.string(),
});

const availabilitySchema = z
  .object({
    key: availabilityValueSchema.nullable(),
    name: availabilityValueSchema.nullable(),
  })
  .strict();

export function parseTessaEnvironmentAvailability(
  payload: unknown,
): TessaEnvironmentAvailability {
  const parsed = availabilitySchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(
      'Tessa workspace availability response has an invalid shape.',
    );
  }
  return parsed.data;
}

export function createTessaEnvironmentAvailabilityClient(deps: {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}) {
  return {
    async check(input: {
      environmentKey?: string;
      environmentName?: string;
      signal?: AbortSignal;
    }): Promise<TessaEnvironmentAvailability> {
      const baseUrl = await deps.discoveryApi.getBaseUrl(TESSA_PLUGIN_ID);
      const query = new URLSearchParams();
      if (input.environmentKey !== undefined) {
        query.set(TESSA_ENVIRONMENT_KEY_QUERY_PARAM, input.environmentKey);
      }
      if (input.environmentName !== undefined) {
        query.set(TESSA_ENVIRONMENT_NAME_QUERY_PARAM, input.environmentName);
      }

      const response = await deps.fetchApi.fetch(
        `${baseUrl}${TESSA_ENVIRONMENT_AVAILABILITY_ROUTE_PATH}?${query.toString()}`,
        { signal: input.signal },
      );
      if (!response.ok) {
        throw new Error('Tessa workspace availability request failed.');
      }
      return parseTessaEnvironmentAvailability(await response.json());
    },
  };
}
